import React, { useEffect, useState } from 'react';
import styles from './authenticator-qr-code.module.scss';
import QRCodeCanvas from 'qrcode.react';
import { Hyperlink, PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { copyIcon, infoIcon } from '@workday/canvas-system-icons-web';
import { autoId, CardView } from '@workday/upc-common';
import { OTPFormField, PasscodeFormState } from '../otp-form-field/otp-form-field';
import { useForm } from 'react-hook-form';
import { Authenticator } from '../../slices/types';
import { ID_AUTHENTICATOR_VERIFY_BUTTON } from '../../autoIds';
import { useTranslation } from 'react-i18next';
import { UseMutationStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useErrorHandling } from '../../hooks';
import { serverError } from '../../utils/server-error';
import { Tooltip } from '@workday/canvas-kit-react';
import { OverflowTooltip } from '@workday/canvas-kit-react/tooltip';

const AUTHENTICATOR_COMPANY = 'Workday';
const ID_AUTHENTICATOR_KEY = 'authenticator-key';
export const ID_COPY_ACCOUNT = 'copy-account';
export const ID_COPY_SECRET = 'copy-secret';

export interface AuthenticatorQrCodeProps {
    title: string;
    authenticator: Authenticator;
    onSubmit: (values: PasscodeFormState) => void;
    mutation: UseMutationStateResult<any, any>;
    disabled?: boolean;
}

export const AuthenticatorQrCode = ({
    title,
    authenticator,
    onSubmit,
    disabled,
    mutation
}: AuthenticatorQrCodeProps) => {
    const { qrCodeUri, secret } = authenticator;
    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    const { t } = useTranslation('shared');

    const [width, setWidth] = useState<number>(window.innerWidth);

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }
    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const isMobile = width <= 768;
    function onCopy(value) {
        navigator.clipboard.writeText(value);
    }

    useErrorHandling(mutation.error, {
        inlineErrors: ['UPC-1005'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    function onHandleSubmit(values) {
        onSubmit(values);
    }

    return (
        <form className={styles.authenticatorQrCode} onSubmit={handleSubmit(onHandleSubmit)}>
            <CardView>
                <h1 className={styles.title}>{title}</h1>
                <p className={styles.desc}>{t('Authenticator.description')}</p>
                <hr className={styles.divider} />
                {!isMobile ? (
                    <div>
                        <h2 className={styles.stepDesc}>{t('Authenticator.setUpStep1')}</h2>
                        <p className={styles.instructions}>{t('Authenticator.totpInstructionsStep1')}</p>
                        {qrCodeUri && (
                            <QRCodeCanvas
                                data-testid={'qr-code-canvas'}
                                className={styles.qrCode}
                                value={qrCodeUri}
                                size={200}
                            />
                        )}
                        <div className={styles.cantScan}>
                            {t('Authenticator.cannotScanMessage')}
                            <Tooltip
                                title={
                                    <div className={styles.tooltipContent}>
                                        {t('Authenticator.manuallyEnterDescription')}
                                    </div>
                                }
                                placement='bottom'
                                className={styles.tooltip}>
                                <TertiaryButton
                                    icon={infoIcon}
                                    iconPosition='end'
                                    size='small'
                                    className={styles.infoButton}
                                />
                            </Tooltip>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h2 className={styles.stepDesc}>{t('Authenticator.setUpStep1Mobile')}</h2>
                    </div>
                )}
                <div className={styles.cell}>
                    <label>{t('Authenticator.Account')}</label>
                    <span className={styles.account}>{AUTHENTICATOR_COMPANY}</span>
                    {navigator.clipboard && (
                        <span className={styles.copyButtonWrapper}>
                            <TertiaryButton
                                type='button'
                                {...autoId(ID_COPY_ACCOUNT)}
                                icon={copyIcon}
                                aria-label={t('Authenticator.copyAccount')}
                                className={styles.copyButton}
                                disabled={disabled}
                                onClick={() => onCopy(AUTHENTICATOR_COMPANY)}
                                size='small'
                            />
                        </span>
                    )}
                </div>
                <div className={styles.cell}>
                    <label>{t('Authenticator.Key')}</label>
                    <span {...autoId(ID_AUTHENTICATOR_KEY)} className={styles.secret}>
                        <span>{secret.slice(0, 4)}</span>
                        <span>{secret.slice(4, 8)}</span>
                        <span>{secret.slice(8, 12)}</span>
                        <span>{secret.slice(12, 16)}</span>
                    </span>
                    {navigator.clipboard && (
                        <span className={styles.copyButtonWrapper}>
                            <TertiaryButton
                                type='button'
                                {...autoId(ID_COPY_SECRET)}
                                aria-label={t('Authenticator.copySecret')}
                                icon={copyIcon}
                                className={styles.copyButton}
                                disabled={disabled}
                                onClick={() => onCopy(secret)}
                                size='small'
                            />
                        </span>
                    )}
                </div>
                <hr className={styles.divider} />
                <h2 className={styles.stepDesc}>{t('Authenticator.setUpStep2')}</h2>
                <p className={styles.instructions}>
                    {t('Authenticator.totpInstructionsStep2')}
                    <span className={styles.learnMoreLink}>
                        <Hyperlink
                            href='faq#how_do_I_set_up_an_authenticator_app'
                            target='_blank'
                            rel='noopener noreferrer'>
                            {t('Authenticator.learnMore')}
                        </Hyperlink>
                    </span>
                </p>
                <OTPFormField
                    grow={true}
                    autoFocus={true}
                    disabled={!!disabled}
                    control={control}
                    className={styles.otpFormField}
                />
                <OverflowTooltip>
                    <PrimaryButton
                        className={styles.submitButton}
                        {...autoId(ID_AUTHENTICATOR_VERIFY_BUTTON)}
                        disabled={disabled}
                        size='large'
                        grow={true}
                        type='submit'>
                        {t('Authenticator.connectDescription')}
                    </PrimaryButton>
                </OverflowTooltip>
            </CardView>
        </form>
    );
};
